/**
 * Phase 18.16-A — Online betalen PageBanner wrapper.
 *
 * Hydrates the banner copy + badges from
 *   GET /api/website/online-payment-settings
 * and renders <PageBanner>. Production-safe fallbacks keep the page
 * looking identical to today on cold-start / network errors.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import PageBanner from "./PageBanner";
import {
  CreditCard, ShieldCheck, Zap, Receipt, CheckCircle2, Clock,
  Banknote, Headphones, Sparkles, Info, Package,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_ICON_REGISTRY = {
  shield: ShieldCheck,
  zap: Zap,
  receipt: Receipt,
  check: CheckCircle2,
  clock: Clock,
  banknote: Banknote,
  support: Headphones,
  sparkles: Sparkles,
  info: Info,
  package: Package,
};
const resolveBadgeIcon = (key) =>
  BADGE_ICON_REGISTRY[String(key || "").toLowerCase()] || ShieldCheck;

const FALLBACK = {
  eyebrow: "Online betalen",
  title: "Betaal veilig",
  highlight: "online",
  subtitle:
    "Betaal direct voor uw zending, factuur of openstaande kosten. Vermeld altijd uw W-nummer of factuurnummer.",
  badges: [
    { icon: "shield", label: "Veilig betalen" },
    { icon: "zap", label: "Snelle verwerking" },
    { icon: "receipt", label: "W-nummer of factuurnummer verplicht" },
  ],
};

export default function OnlinePaymentPageBanner() {
  const [banner, setBanner] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/online-payment-settings`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const pb = res?.data?.sections?.page_banner;
        if (pb && typeof pb === "object") {
          setBanner((prev) => ({
            ...prev,
            ...pb,
            badges: Array.isArray(pb.badges) ? pb.badges : prev.badges,
          }));
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <PageBanner
      eyebrow={banner.eyebrow}
      eyebrowIcon={CreditCard}
      title={banner.title}
      highlight={banner.highlight}
      description={banner.subtitle}
      compact
      testid="online-betalen-banner"
    >
      {Array.isArray(banner.badges) && banner.badges.length > 0 ? (
        <ul className="flex flex-wrap gap-2 mt-2" data-testid="online-betalen-banner-badges">
          {banner.badges.map((b, i) => {
            const Ico = resolveBadgeIcon(b.icon);
            return (
              <li
                key={i}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[12px] tracking-wide font-semibold text-white"
              >
                <Ico className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.25} />
                {b.label}
              </li>
            );
          })}
        </ul>
      ) : null}
    </PageBanner>
  );
}
